/**
 * Parse uploaded file (Excel or CSV) and return preview rows + metadata.
 * Used by Upload Preview popup only. No API calls.
 */
import * as XLSX from 'xlsx';

const PREVIEW_ROW_COUNT = 5;

function normalizeKey(str) {
  return String(str ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

function isExcelSerialNumber(v) {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

function excelSerialToYMD(serial) {
  try {
    // Prefer SSF formatter when available (keeps correct calendar output)
    const formatted = XLSX.SSF.format('yyyy-mm-dd', serial);
    if (formatted && typeof formatted === 'string') return formatted;
  } catch {
    // fall back below
  }
  // Fallback conversion (Excel epoch)
  const d = new Date((serial - 25569) * 86400 * 1000);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function maybeConvertDateCell(headerKey, rawValue) {
  const k = normalizeKey(headerKey);
  const dateKeys = new Set([
    'invoicedate',
    'manufacturingdate',
    'expirydate',
    'vehiclereportingdate',
  ]);
  if (!dateKeys.has(k)) return rawValue;

  // If numeric, treat as Excel serial date
  if (isExcelSerialNumber(rawValue)) {
    return excelSerialToYMD(rawValue);
  }

  // If string that is numeric (e.g. "46111"), convert too
  if (typeof rawValue === 'string') {
    const s = rawValue.trim();
    if (s && /^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (isExcelSerialNumber(n)) return excelSerialToYMD(n);
    }
  }

  return rawValue;
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { headers: [], rows: [], totalRows: 0 };
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  const allRows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, j) => {
      row[h] = maybeConvertDateCell(h, values[j] ?? '');
    });
    allRows.push(row);
  }
  return { headers, rows: allRows.slice(0, PREVIEW_ROW_COUNT), allRows, totalRows: allRows.length };
}

export function parseFileToPreview(file) {
  return new Promise((resolve, reject) => {
    const name = file.name || '';
    const isExcel = /\.(xlsx|xls)$/i.test(name);
    const isCSV = /\.csv$/i.test(name);

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
          if (json.length === 0) {
            resolve({ headers: [], rows: [], fileName: name });
            return;
          }
          const headers = (json[0] || []).map((h) => String(h ?? '').trim());
          const allRows = [];
          for (let i = 1; i < json.length; i++) {
            const arr = json[i] || [];
            const row = {};
            headers.forEach((key, j) => {
              const raw = arr[j] !== undefined && arr[j] !== null ? arr[j] : '';
              const converted = maybeConvertDateCell(key, raw);
              row[key] = converted !== undefined && converted !== null ? String(converted) : '';
            });
            allRows.push(row);
          }
          resolve({
            headers,
            rows: allRows.slice(0, PREVIEW_ROW_COUNT),
            allRows,
            totalRows: allRows.length,
            fileName: name,
          });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    } else if (isCSV) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const { headers, rows, allRows, totalRows } = parseCSV(e.target.result || '');
          resolve({ headers, rows, allRows, totalRows, fileName: name });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file, 'UTF-8');
    } else {
          reject(new Error('Please select an Excel (.xlsx, .xls) or CSV file'));
    }
  });
}
